// Rollout Readout: did a launch change an event rate, without an A/B platform? Counts
// over exposure in two groups (before and after a launch, or accounts that did and did
// not get it) give Gamma posteriors on the two rates (Jeffreys prior). A before/after
// also needs the rate's own swing from period to period before the launch: pasted
// pre-launch periods measure it (log-rate variance beyond Poisson noise, with a weak
// prior worth one period), and it widens the comparison of the two windows. An extra
// allowance covers one-off changes the history cannot show. Without history, only that
// allowance stands, and the page says the range rests on a judgement. The result is a
// "change after launch", never an effect. A randomized split has no drift term.
// Source: docs/backlog-prioritization-research.md#12-without-an-ab-testing-platform. Tests: model.test.js.

import { Rng } from "../lib/rng.js";
import { Z90, meanOf, quantilesOf, shareWhere } from "../lib/ranges.js";

export const DRAWS = 40000;
export const SEED = 20261017;
export const MODES = ["before-after", "randomized"];
/** Weak prior on the period-to-period sd of the log rate (about ±16%), worth one period. */
export const SWING_PRIOR = { sd: 0.1, periods: 1 };
export const MAX_PERIODS = 260;

/**
 * Pre-launch periods from text, one per line as "events/exposure" (or "events exposure").
 * Lines that cannot be read are reported, not dropped in silence.
 * @param {string} text
 * @returns {{ periods: {events: number, exposure: number}[], excluded: {line: string, reason: string}[] }}
 */
export function parsePeriods(text) {
  const periods = [];
  const excluded = [];
  for (const raw of String(text ?? "").split(/\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const parts = line.split(/[\s/,;]+/).filter(Boolean).map(Number);
    if (parts.length !== 2 || !parts.every(Number.isFinite)) excluded.push({ line, reason: "expected two numbers: events and exposure" });
    else if (!(Number.isInteger(parts[0]) && parts[0] >= 0)) excluded.push({ line, reason: "events must be a whole number of 0 or more" });
    else if (!(parts[1] > 0)) excluded.push({ line, reason: "exposure must be above 0" });
    else if (parts[0] === 0) excluded.push({ line, reason: "a period with no events says nothing about the swing of the log rate" });
    else if (periods.length >= MAX_PERIODS) excluded.push({ line, reason: `only the first ${MAX_PERIODS} periods are used` });
    else periods.push({ events: parts[0], exposure: parts[1] });
  }
  return { periods, excluded };
}

/**
 * Swing of the rate between periods: the variance of the log rate across periods less
 * its Poisson part (about 1/events per period), as a sum of squares and its degrees of
 * freedom, so a posterior can combine it with SWING_PRIOR.
 * @param {{events: number, exposure: number}[]} periods at least 2
 * @returns {{ n: number, ss: number, dof: number, sdHat: number }}
 */
export function periodSwing(periods) {
  const logs = periods.map((p) => Math.log(p.events / p.exposure));
  const n = logs.length;
  const mean = meanOf(logs);
  const raw = logs.reduce((s, x) => s + (x - mean) ** 2, 0);
  const poisson = periods.reduce((s, p) => s + 1 / p.events, 0) * ((n - 1) / n);
  const ss = Math.max(0, raw - poisson);
  return { n, ss, dof: n - 1, sdHat: Math.sqrt(ss / (n - 1)) };
}

/**
 * Problems with the counts, in words; empty when usable.
 * @param {{ events: number, exposure: number }} g
 * @param {string} name
 * @returns {string[]}
 */
export function groupIssues(g, name) {
  const out = [];
  if (!(Number.isInteger(g.events) && g.events >= 0)) out.push(`${name}: events must be a whole number of 0 or more.`);
  if (!(g.exposure > 0)) out.push(`${name}: exposure must be above 0.`);
  return out;
}

/** sd of the log drift factor from a ±percent 90% allowance. */
export function driftSd(driftPct) {
  return Math.log(1 + Math.max(0, driftPct) / 100) / Z90;
}

/**
 * @param {{ mode: string, history: string, afterPeriods: number, base: {events:number, exposure:number}, test: {events:number, exposure:number}, driftPct: number, higherIsBetter: boolean, targetPct: number }} s
 * @param {{ draws?: number, seed?: number }} [opts]
 * @returns {object}
 */
export function analyseRollout(s, { draws = DRAWS, seed = SEED } = {}) {
  const randomized = s.mode === "randomized";
  const names = randomized ? ["Without the feature", "With the feature"] : ["Before", "After"];
  const history = randomized ? { periods: [], excluded: [] } : parsePeriods(s.history);
  const measured = history.periods.length >= 2;
  const base = measured
    ? { events: history.periods.reduce((a, p) => a + p.events, 0), exposure: history.periods.reduce((a, p) => a + p.exposure, 0) }
    : s.base;
  const issues = [...(measured ? [] : groupIssues(base, names[0])), ...groupIssues(s.test, names[1])];
  if (!measured && !randomized && history.periods.length === 1) issues.push("Paste at least two pre-launch periods to measure the swing, or clear the box.");
  if (measured && !(s.afterPeriods >= 1)) issues.push("Say how many periods the after window covers.");
  if (issues.length) return { issues, names, history, base, driftFrom: randomized ? "none" : measured ? "measured" : "judgement" };

  const extra = randomized ? 0 : driftSd(s.driftPct);
  const swing = measured ? periodSwing(history.periods) : null;
  // Comparing a window of kb periods with one of ka periods: each window's mean log rate
  // carries swing²/k of period-to-period variation.
  const windows = measured ? 1 / history.periods.length + 1 / s.afterPeriods : 0;
  const dof = measured ? SWING_PRIOR.periods + swing.dof : 0;
  const scale2 = measured ? (SWING_PRIOR.periods * SWING_PRIOR.sd ** 2 + swing.ss) / dof : 0;
  const rng = new Rng(seed);
  const change = new Float64Array(draws);
  const dataOnly = new Float64Array(draws);
  for (let d = 0; d < draws; d++) {
    const l0 = rng.gamma(0.5 + base.events, base.exposure);
    const l1 = rng.gamma(0.5 + s.test.events, s.test.exposure);
    const ratio = l1 / l0;
    dataOnly[d] = ratio - 1;
    const swing2 = measured ? (dof * scale2) / (2 * rng.gamma(dof / 2, 1)) : 0;
    const b = Math.sqrt(swing2 * windows + extra * extra);
    change[d] = ratio * Math.exp(b * rng.normal()) - 1;
  }
  const [c5, c50, c95] = quantilesOf(change, [0.05, 0.5, 0.95]);
  const [d5, d95] = quantilesOf(dataOnly, [0.05, 0.95]);
  const sign = s.higherIsBetter ? 1 : -1;
  const target = Math.max(0, s.targetPct) / 100;
  const width = c95 - c5;
  return {
    issues,
    names,
    history,
    base,
    // Where the drift term comes from: measured pre-launch swing, a judgement only, or none (randomized).
    driftFrom: randomized ? "none" : measured ? "measured" : "judgement",
    swingPct: measured ? (Math.exp(Math.sqrt(scale2)) - 1) * 100 : null,
    rates: { base: base.events / base.exposure, test: s.test.events / s.test.exposure },
    median: c50,
    low: c5,
    high: c95,
    mean: meanOf(change),
    chanceBetter: shareWhere(change, (x) => sign * x > 0),
    chanceMatters: shareWhere(change, (x) => sign * x > target),
    chanceHarm: shareWhere(change, (x) => sign * x < -target),
    driftShare: width > 0 ? Math.max(0, 1 - (d95 - d5) / width) : 0,
    dataOnly: { low: d5, high: d95 },
  };
}

/** A relative change as a signed percentage. */
export function signedPct(x) {
  if (!Number.isFinite(x)) return "—";
  const v = Math.round(x * 1000) / 10;
  return `${v > 0 ? "+" : ""}${v}%`;
}
export const pct = (p) => (Number.isFinite(p) ? `${Math.round(p * 100)}%` : "—");

/**
 * Plain sentences for a ticket, a channel or an agent's prompt.
 * @param {object} r analyseRollout result
 * @param {object} s page state
 * @returns {string[]}
 */
export function summaryLines(r, s) {
  if (r.issues.length) return [];
  const what = s.mode === "randomized" ? "Effect of the feature" : "Change after the launch (not an effect: other things changed too)";
  const lines = [
    `${what} on ${s.eventLabel}: ${signedPct(r.median)}, 90% range ${signedPct(r.low)} to ${signedPct(r.high)}.`,
    `Chance it moved the right way: ${pct(r.chanceBetter)}; by more than ${s.targetPct}%: ${pct(r.chanceMatters)}; harm beyond ${s.targetPct}%: ${pct(r.chanceHarm)}.`,
  ];
  if (r.driftFrom === "measured")
    lines.push(`Before the launch the rate swung about ±${Math.round(r.swingPct)}% from period to period (${r.history.periods.length} periods); with ${s.driftPct > 0 ? `an extra ±${s.driftPct}% allowance` : "no extra allowance"}, that swing is ${pct(r.driftShare)} of the range's width.`);
  else if (r.driftFrom === "judgement")
    lines.push(`No pre-launch history was given: the range rests on a judged ±${s.driftPct}% for everything else that moved the rate, not on measured variation.`);
  return lines;
}
